// src/Components/ResidentDashboard.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  query,
  collection,
  where,
} from "firebase/firestore";
import {
  Card,
  Table,
  Button,
  Alert,
  Spinner,
  Nav,
  Row,
  Col,
  Badge,
  Container,
  Form,
} from "react-bootstrap";
import { useAuth } from "../rbac/AuthContext";
import ProfileSection from "./Resident/ProfileSection";
import EventsPage from "./events/EventsPage";
import "../styles/theme.css"
import UnverifiedBanner from "./Resident/UnverifiedBanner";

const formatTs = (ts) => {
  try {
    return ts?.toDate?.()?.toLocaleString?.() || "—";
  } catch {
    return "—";
  }
};

export default function ResidentDashboard() {
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);
  const { primaryCommunityId } = useAuth();

  // Auth / user
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState("");
  const [userDoc, setUserDoc] = useState(null);

  // Community
  const [communityLoading, setCommunityLoading] = useState(true);
  const [communityError, setCommunityError] = useState("");
  const [communityDoc, setCommunityDoc] = useState(null);

  // Events
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");
  const [events, setEvents] = useState([]);

  // Tabs
  const [activeTab, setActiveTab] = useState("dashboard"); // 'dashboard' | 'events' | 'services' | 'profile'

  // --- Auth state ---
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setCurrentUser(u || null);
    });
    return () => unsub();
  }, [auth]);

  // --- Load user doc ---
  useEffect(() => {
    const loadUser = async () => {
      if (!currentUser?.uid) {
        setUserDoc(null);
        setUserLoading(false);
        return;
      }
      setUserLoading(true);
      setUserError("");
      try {
        const ref = doc(db, "users", currentUser.uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setUserError("User profile not found.");
          setUserDoc(null);
        } else {
          setUserDoc({ uid: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error("Load user error:", err);
        setUserError(err?.message || "Failed to load user.");
        setUserDoc(null);
      } finally {
        setUserLoading(false);
      }
    };
    loadUser();
  }, [currentUser]);

  // Prefer userDoc.unit.communityId; fall back to primaryCommunityId (if provided by AuthContext)
  const communityId = userDoc?.unit?.communityId || primaryCommunityId || null;
  const flatLabel = userDoc?.unit?.flatLabel || null;
  const blockName = userDoc?.unit?.blockName || null;

  // --- Subscribe to community doc ---
  useEffect(() => {
    if (!communityId) {
      setCommunityDoc(null);
      setCommunityLoading(false);
      return;
    }
    setCommunityLoading(true);
    setCommunityError("");
    const ref = doc(db, "communities", communityId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setCommunityError("Community not found.");
          setCommunityDoc(null);
          setCommunityLoading(false);
          return;
        }
        setCommunityDoc({ id: snap.id, ...snap.data() });
        setCommunityLoading(false);
      },
      (err) => {
        console.error("Community snapshot error:", err);
        setCommunityError(err?.message || "Failed to load community.");
        setCommunityLoading(false);
      }
    );
    return () => unsub();
  }, [communityId]);

  // --- Subscribe to events for this community ---
  useEffect(() => {
    if (!communityId) {
      setEvents([]);
      setEventsLoading(false);
      return;
    }
    setEventsLoading(true);
    setEventsError("");

    const q = query(collection(db, "events"), where("communityId", "==", communityId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(rows);
        setEventsLoading(false);
      },
      (err) => {
        console.error("Events snapshot error:", err);
        setEventsError(err?.message || "Failed to load events.");
        setEventsLoading(false);
      }
    );

    return () => unsub();
  }, [communityId]);

  // --- Logout ---
  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      // Optionally route to login page: navigate("/login");
    } catch (err) {
      alert(err?.message || "Failed to log out.");
    }
  }, [auth]);

  // --- Derived UI bits ---
  const communityName = communityDoc?.name || "—";
  const communityAddress = communityDoc?.address || "—";
  const communityDesc = communityDoc?.description || "";

  const isPending = userDoc?.status === "pending";
  const isApproved = userDoc?.status === "approved";
  const statusBadge = isApproved ? (
    <Badge bg="success">Approved</Badge>
  ) : isPending ? (
    <Badge bg="warning">Pending</Badge>
  ) : (
    <Badge bg="secondary">{userDoc?.status || "—"}</Badge>
  );

  // --- Views ---
  const DashboardView = () => (
    <div>
      <Row className="g-3">
        <Col md={3}>
          <Card bg="light"><Card.Body>
            <h6 className="text-muted">Members</h6>
            <h3>{Array.isArray(communityDoc?.members) ? communityDoc.members.length : 0}</h3>
          </Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card bg="light"><Card.Body>
            <h6 className="text-muted">Association</h6>
            <h3>{communityDoc?.association?.length || 0}</h3>
          </Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card bg="light"><Card.Body>
            <h6 className="text-muted">Events</h6>
            <h3>{communityDoc?.events?.length || events.length || 0}</h3>
          </Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card bg="light"><Card.Body>
            <h6 className="text-muted">Blocks</h6>
            <h3>{communityDoc?.blocks?.length || 0}</h3>
          </Card.Body></Card>
        </Col>
      </Row>

      {/* <Card className="mt-3">
        <Card.Body>
          <div><strong>Community ID:</strong> <code>{communityDoc?.id || "—"}</code></div>
          <div><strong>Created:</strong> {formatTs(communityDoc?.createdAt)}</div>
        </Card.Body>
      </Card> */}
    </div>
  );

  const EventsView = () => (
    <div>
      <h5 className="mb-3">Community Events</h5>
      {eventsError && <Alert variant="danger">{eventsError}</Alert>}

      {eventsLoading ? (
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading events…</span>
        </div>
      ) : events.length === 0 ? (
        <Alert variant="info">No events available.</Alert>
      ) : (
        <Table bordered hover responsive size="sm">
          <thead>
            <tr>
              <th>Title</th>
              <th>When</th>
              <th>Where</th>
              <th>Details</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev, idx) => (
              <tr key={`event-${ev?.id || "missing"}-${idx}`}>
                <td>{ev?.name || ev?.title || "—"}</td>
                <td>{formatEventDate(ev?.startAt, ev?.endAt) || ev?.date || "—"}</td>
                <td>{ev?.location || "—"}</td>
                <td className="text-truncate" style={{ maxWidth: 360 }}>
                  {ev?.description || "—"}
                </td>
                <td className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate(`/community/${communityDoc?.id}/events/${ev?.id}`)}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );

  const ServicesView = () => (
    <div>
      <h5 className="mb-3">Services</h5>
      <Alert variant="secondary">
        Services module is not developed yet. We’ll update this soon.
      </Alert>
    </div>
  );

  // --- Helpers ---
  function formatEventDate(startAt, endAt) {
    try {
      const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
      const s = toDate(startAt);
      const e = toDate(endAt);
      if (!s && !e) return "—";
      if (s && !isNaN(s)) {
        if (e && !isNaN(e)) return `${s.toLocaleString()} – ${e.toLocaleString()}`;
        return s.toLocaleString();
      }
      return "—";
    } catch {
      return "—";
    }
  }

  // --- Guard: not logged in ---
  if (currentUser === null) {
    return (
      <Container className="mt-4">
        <Card>
          <Card.Body>
            <Alert variant="warning">You are not logged in.</Alert>
            {/* Optionally: navigate("/login") */}
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const coreLoading = userLoading || communityLoading;

  // --- Mobile-only sticky, scrollable tab bar (Dashboard, Events, Services, Profile) ---
  const MobileTabs = (
    <div className="sticky-mobile-tabs bg-white border-bottom d-md-none mt-5">
      <div className="px-2 py-2 mobile-nav-scroll">
        <Nav
          variant="pills"
          className="flex-nowrap"
          activeKey={activeTab}
          onSelect={(k) => {
            if (!k) return;
            setActiveTab(k);
            // Scroll to top for nicer UX on mobile
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <Nav.Item>
            <Nav.Link eventKey="dashboard" className="text-capitalize">Dashboard</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="events" className="text-capitalize">Events</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="services" className="text-capitalize">Services</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="profile" className="text-capitalize">Settings</Nav.Link>
          </Nav.Item>
        </Nav>
      </div>
    </div>
  );

  return (
    <Container fluid className="mt-3">

      <UnverifiedBanner communityId={communityId} />
      {/* Inline styles for the mobile tabs */}
      <style>{`
        .mobile-nav-scroll {
          overflow-x: auto;
          white-space: nowrap;
          -webkit-overflow-scrolling: touch;
        }
        /* Ensure tabs sit below your fixed header; adjust to your header height if needed */
        .sticky-mobile-tabs {
          position: sticky;
          top: 60px;
          z-index: 1020;
          marginTop : 3rem;
        }
        .mobile-nav-scroll .nav-link {
          padding: .5rem .75rem;
          font-weight: 600;
        }
      `}</style>

      {/* Mobile horizontal tabs */}
      {MobileTabs}

      <Card className="mb-3 d-md-none" id="res-dash-header">
        <Card.Body>
          <Row>
            <Col md={8}>
              <h5 className="mb-2">
                {communityName} {statusBadge}
              </h5>
              <div className="text-muted">{communityAddress}</div>
              {communityDesc && <div className="mt-2">{communityDesc}</div>}
            </Col>
            <Col md={4}>
              <div className="border p-2 rounded">
                <div className="fw-semibold mb-2">Your Flat</div>
                <div>Block: <strong>{blockName || "—"}</strong></div>
                <div>Flat: <strong>{flatLabel || "—"}</strong></div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        {/* Left sidebar — hidden on mobile, unchanged on desktop */}
        <Col md={3} lg={2} className="d-none d-md-block">
          <Card>
            <Card.Body className="p-0">
              <Nav className="flex-column">
                <Nav.Link
                  active={activeTab === "dashboard"}
                  onClick={() => setActiveTab("dashboard")}
                  className="py-2 px-3"
                >
                  Dashboard
                </Nav.Link>
                <Nav.Link
                  active={activeTab === "events"}
                  onClick={() => setActiveTab("events")}
                  className="py-2 px-3"
                >
                  Events
                </Nav.Link>
                <Nav.Link
                  active={activeTab === "services"}
                  onClick={() => setActiveTab("services")}
                  className="py-2 px-3"
                >
                  Services
                </Nav.Link>
                <Nav.Link
                  active={activeTab === "profile"}
                  onClick={() => setActiveTab("profile")}
                  className="py-2 px-3"
                >
                  Settings
                </Nav.Link>
              </Nav>

              <div className="border-top mt-2" />

              <div className="p-3">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleLogout}
                  className="w-100"
                >
                  Logout
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right content */}
        <Col md={9} lg={10}>
          {coreLoading ? (
            <div className="d-flex align-items-center mt-3">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Loading your dashboard…</span>
            </div>
          ) : userError ? (
            <Alert variant="danger" className="mt-3">{userError}</Alert>
          ) : communityError ? (
            <Alert variant="danger" className="mt-3">{communityError}</Alert>
          ) : (
            <>
              {activeTab === "dashboard" && <DashboardView />}
              {activeTab === "events" && <EventsView />}
              {activeTab === "services" && <ServicesView />}
              {activeTab === "profile" && (
                <ProfileSection currentUser={currentUser} userDoc={userDoc} />
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}
